import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import ArticleTemplate from "@/components/ArticleTemplate";
import { Article, getArticleBySlug, getRelatedArticles } from "@/data/articlesData";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";

// Import specific article page components
import KorkojenNousuArticlePage from "./KorkojenNousuArticlePage";
import KoronNousunTaituminenArticlePage from "./KoronNousunTaittuminenArticlePage";
import YhdistelylainaOpasArticlePage from "./YhdistelylainaOpasArticlePage";
import LainavertailuOpasArticlePage from "./LainavertailuOpasArticlePage";
import KulutusluottoVertailuArticlePage from "./KulutusluottoVertailuArticlePage";
import HauskaLomalainaArticlePage from "./HauskaLomalainaArticlePage";
import VenelainaOpasArticlePage from "./VenelainaOpasArticlePage";
import LomakohteetArticlePage from "./LomakohteetArticlePage";
import AsuntomarkkinatEnnuste2025ArticlePage from "./AsuntomarkkinatEnnuste2025ArticlePage";
import UlosotonAbcArticlePage from "./UlosotonAbcArticlePage";
import CaravanMatkailuSuomessa2025ArticlePage from "./CaravanMatkailuSuomessa2025ArticlePage";
import VastuullinenLainaaminenArticlePage from "./VastuullinenLainaaminenArticlePage";

// Map of slug to component for special case articles with custom templates
const CUSTOM_ARTICLE_PAGES: Record<string, React.ComponentType> = {
  "korkojen-nousu-ja-inflaatio": KorkojenNousuArticlePage,
  "koron-nousun-taittuminen-mita-lainanottajan-tulisi-tietaa": KoronNousunTaituminenArticlePage,
  "yhdistelylaina-opas-lainojen-yhdistamiseen": YhdistelylainaOpasArticlePage,
  "miten-valita-oikea-laina-eri-tilanteisiin-2025": LainavertailuOpasArticlePage,
  "kulutusluottojen-vertailu-saasta-korkokuluissa": KulutusluottoVertailuArticlePage,
  "hauska-lomalaina-rahoita-unelmalomasi-suomen-parhaisiin-luontokohteisiin": HauskaLomalainaArticlePage,
  "venelainaopas-nain-rahoitat-unelmiesi-veneen": VenelainaOpasArticlePage,
  "suosituimmat-lomakohteet-2025": LomakohteetArticlePage,
  "asuntomarkkinoiden-ennuste-2025-kannattaako-nyt-ostaa-vai-myyda": AsuntomarkkinatEnnuste2025ArticlePage,
  "ulosoton-abc-kaikki-mita-sinun-tulee-tietaa-ulosotosta": UlosotonAbcArticlePage,
  "caravan-matkailu-suomessa-2025": CaravanMatkailuSuomessa2025ArticlePage,
  "vastuullinen-lainaaminen-nain-teet-kestavia-rahoituspaatoksia": VastuullinenLainaaminenArticlePage,
};

// Map of legacy slugs to their current versions
const LEGACY_SLUG_REDIRECTS: Record<string, string> = {
  "korkojen-nousu-ja-inflaatio-nain-suojaat-talouttasi": "korkojen-nousu-ja-inflaatio",
  "koron-nousun-taittuminen-mita-lainanottajan-tulisi-tietaa": "koron-nousun-taittuminen",
  "lainojen-vertailu-nain-loydat-parhaan-lainan": "miten-valita-oikea-laina-eri-tilanteisiin-2025",
  "yhdistelylaina-opas": "yhdistelylaina-opas-lainojen-yhdistamiseen",
  "lainavertailu-opas": "miten-valita-oikea-laina-eri-tilanteisiin-2025",
};

const ArticlePage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [article, setArticle] = useState<Article | null>(null);
  const [relatedArticles, setRelatedArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    console.log(`ArticlePage: Loading article with slug: ${slug}`);
    
    if (!slug) {
      navigate('/artikkelit');
      return;
    }
    
    // Handle legacy slug redirects
    if (LEGACY_SLUG_REDIRECTS[slug]) {
      navigate(`/artikkelit/${LEGACY_SLUG_REDIRECTS[slug]}`);
      return;
    }
    
    // Check if this is a special case article with custom template
    if (CUSTOM_ARTICLE_PAGES[slug]) {
      console.log(`ArticlePage: Found custom page for slug: ${slug}`);
      setLoading(false);
      return;
    }
    
    const foundArticle = getArticleBySlug(slug);
    if (foundArticle) {
      console.log(`ArticlePage: Found article: ${foundArticle.title}`);
      setArticle(foundArticle);
      setRelatedArticles(getRelatedArticles(foundArticle.id));
    } else {
      console.log(`ArticlePage: No article found for slug: ${slug}, redirecting to articles listing`);
      // Article not found, redirect to articles listing
      navigate('/artikkelit');
    }
    setLoading(false);
  }, [slug, navigate]);
  
  // Render custom article page if this slug has a dedicated component
  if (slug && CUSTOM_ARTICLE_PAGES[slug]) {
    console.log(`ArticlePage: Rendering custom component for slug: ${slug}`);
    const CustomArticlePage = CUSTOM_ARTICLE_PAGES[slug];
    return <CustomArticlePage />;
  }
  
  if (loading || !article) {
    return (
      <div className="container mx-auto p-8 flex justify-center items-center min-h-[60vh]">
        <div className="animate-pulse text-lg">Ladataan artikkelia...</div>
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen py-8">
      <div className="container mx-auto px-4">
        <div className="mb-8">
          <Button variant="outline" asChild className="mb-4">
            <Link to="/artikkelit" className="flex items-center">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Takaisin artikkeleihin
            </Link>
          </Button>
          
          <ArticleTemplate
            title={article.title}
            description={article.description}
            date={article.date}
            readTime={article.readTime}
            category={article.category}
            imageUrl={article.image}
            relatedArticles={relatedArticles.map(ra => ({
              id: ra.id,
              title: ra.title,
              slug: ra.slug,
              description: ra.summary || ra.description
            }))}
            content={<ArticleContent slug={slug} />}
            showEditButton={false}
          />
        </div>
      </div>
    </div>
  );
};

// Separate component for article content based on slug
const ArticleContent: React.FC<{ slug: string }> = ({ slug }) => {
  // This function returns the article content based on slug
  // In a real implementation, this would fetch or render the specific article content
  return (
    <div className="prose max-w-none">
      <p className="text-lg">Tämän artikkelin sisältö on vielä valmisteilla.</p>
      <p>Palaa myöhemmin tai tutustu muihin artikkeleihin sivullamme.</p>
      <div className="mt-8 p-4 bg-blue-50 rounded-lg">
        <h3 className="text-xl font-medium text-blue-800">Haluatko lisätietoja?</h3>
        <p className="mb-4">Ota yhteyttä asiantuntijoihimme ja saat apua laina-asioissa.</p>
        <Link to="/yhteystiedot" className="inline-block bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors">
          Ota yhteyttä
        </Link>
      </div>
    </div>
  );
};

export default ArticlePage;
